/**
 * Music — Audio Bible §6: one motif, fifteen faces.
 *
 * Fifteen separate compositions would be fifteen separate costs and fifteen
 * chances to sound like a different game. Instead the main motif (D-A-F-E) is
 * transformed — inverted, augmented, fragmented, retrograded, transposed — so
 * every track is recognisably the same world.
 *
 * These renders are *playable drafts*, good enough to ship a vertical slice and
 * to audition arrangements. Final tracks come from Suno using the same motif and
 * the same arrangement notes (see PROMPTS in tools/asset-pipeline).
 */

import { MOTIF_INTERVALS, MOTIF_ROOT_HZ, semitonesToHz } from './materials.js';
import {
  NoiseRng,
  applyFades,
  createBuffer,
  lowpass,
  makeSeamlessLoop,
  mixInto,
  modalRing,
  noiseSweep,
  normalize,
  shimmer,
  struckTone,
  type Buffer,
} from './synth.js';
import { SAMPLE_RATE } from './sfx.js';

export type MotifTransform =
  | 'original'
  | 'inverted'
  | 'augmented'
  | 'fragmented'
  | 'retrograde'
  | 'rhythmic'
  | 'major'
  | 'octaveUp'
  | 'lowered';

export interface TrackSpec {
  readonly id: string;
  /** Semitone transposition from D. */
  readonly transpose: number;
  readonly bpm: number;
  readonly transform: MotifTransform;
  /** Loop length in seconds (Audio Bible §6: 30-120 s). */
  readonly loopSeconds: number;
  readonly instruments: readonly ('harp' | 'strings' | 'bell' | 'drone' | 'percussion' | 'brass')[];
  /** Human-readable arrangement note, reused verbatim in the Suno prompt. */
  readonly arrangement: string;
}

export const TRACKS: readonly TrackSpec[] = [
  { id: 'mus.main', transpose: 0, bpm: 72, transform: 'original', loopSeconds: 48, instruments: ['harp', 'strings'], arrangement: '하프 + 현 + 단일 목관. 원형 모티프를 느리게.' },
  { id: 'mus.town.aurelia', transpose: 0, bpm: 92, transform: 'inverted', loopSeconds: 40, instruments: ['harp', 'percussion'], arrangement: '류트 + 플루트 + 탬버린. 상행 반전.' },
  { id: 'mus.forest.elden', transpose: 2, bpm: 84, transform: 'octaveUp', loopSeconds: 44, instruments: ['harp', 'strings'], arrangement: '하프 + 현 하모닉스. 3도 위 전조.' },
  { id: 'mus.mountain.iron', transpose: 0, bpm: 78, transform: 'augmented', loopSeconds: 52, instruments: ['strings', 'percussion'], arrangement: '저역 현 + 망치 타악. 2배 길이 증강.' },
  { id: 'mus.dungeon', transpose: 0, bpm: 66, transform: 'fragmented', loopSeconds: 56, instruments: ['drone', 'bell'], arrangement: '드론 + 준비된 피아노. 2음만 남긴 단편.' },
  { id: 'mus.combat', transpose: 0, bpm: 140, transform: 'rhythmic', loopSeconds: 32, instruments: ['percussion', 'strings', 'brass'], arrangement: '타악 + 저역 현 + 브라스 스탭. 스타카토 리듬화.' },
  { id: 'mus.boss', transpose: 0, bpm: 128, transform: 'original', loopSeconds: 40, instruments: ['brass', 'strings', 'percussion'], arrangement: '풀 오케스트라 + 합창. 원형 + 대선율.' },
  { id: 'mus.mystery', transpose: 1, bpm: 52, transform: 'retrograde', loopSeconds: 60, instruments: ['bell', 'drone'], arrangement: '첼레스타 + 역재생 현. 역행.' },
  { id: 'mus.dream', transpose: 0, bpm: 58, transform: 'octaveUp', loopSeconds: 48, instruments: ['bell', 'drone'], arrangement: '첼레스타 + 패드. 높은 옥타브 원형.' },
  { id: 'mus.death', transpose: 0, bpm: 44, transform: 'fragmented', loopSeconds: 36, instruments: ['strings'], arrangement: '솔로 첼로. 마지막 2음만.' },
  { id: 'mus.victory', transpose: 0, bpm: 96, transform: 'major', loopSeconds: 36, instruments: ['brass', 'strings', 'bell'], arrangement: '브라스 + 현 + 벨. 장조 변환.' },
  { id: 'mus.chronicle', transpose: 0, bpm: 60, transform: 'major', loopSeconds: 52, instruments: ['harp', 'strings'], arrangement: '솔로 하프 + 현 패드. 장조 회상.' },
  { id: 'mus.shop', transpose: 0, bpm: 88, transform: 'inverted', loopSeconds: 36, instruments: ['harp', 'percussion'], arrangement: '류트 + 퍼커션. 경쾌한 반전.' },
  { id: 'mus.fateDebt', transpose: -1, bpm: 70, transform: 'lowered', loopSeconds: 44, instruments: ['strings', 'drone'], arrangement: '디튠 현 + 저역 맥동. 반음 낮춤.' },
  { id: 'mus.lastRoom', transpose: 0, bpm: 40, transform: 'original', loopSeconds: 72, instruments: ['drone', 'bell', 'strings', 'brass'], arrangement: '최소 편성에서 전면 확장. 전곡 요소 총집합.' },
];

export const TRACK_BY_ID: ReadonlyMap<string, TrackSpec> = new Map(TRACKS.map((t) => [t.id, t]));

/** Apply a transform to the motif, returning semitone offsets. */
export function transformMotif(transform: MotifTransform): number[] {
  const base = [...MOTIF_INTERVALS];
  switch (transform) {
    case 'original':
    case 'rhythmic':
      return base;
    case 'inverted':
      // Mirror each interval around the root: descending becomes ascending.
      // `|| 0` normalises -0, which is the same pitch but compares unequal.
      return base.map((n) => -n || 0);
    case 'augmented':
      // Same pitches; the caller doubles note length via `augmented` below.
      return base;
    case 'fragmented':
      return base.slice(-2);
    case 'retrograde':
      return [...base].reverse();
    case 'major':
      // Raise the minor third to a major third — the one change that flips mood.
      return base.map((n) => (n === 3 ? 4 : n));
    case 'octaveUp':
      return base.map((n) => n + 12);
    case 'lowered':
      return base.map((n) => n - 1);
  }
}

function instrumentVoice(
  sampleRate: number,
  kind: TrackSpec['instruments'][number],
  freq: number,
  seconds: number,
  amplitude: number,
  rng: NoiseRng,
): Buffer {
  switch (kind) {
    case 'harp':
      return struckTone(sampleRate, freq, seconds, 'marimba', amplitude);
    case 'bell':
      return struckTone(sampleRate, freq, seconds, 'bell', amplitude * 0.8);
    case 'strings': {
      // Sustained and slightly detuned, so it reads as several players.
      const buf = createBuffer(sampleRate, seconds);
      for (const detune of [-7, 0, 6]) {
        mixInto(buf, shimmer(sampleRate, freq * Math.pow(2, detune / 1200), seconds, 5, amplitude * 0.4), 0);
      }
      return lowpass(buf, 4000);
    }
    case 'brass': {
      const buf = createBuffer(sampleRate, seconds);
      // Odd harmonics give the brassy edge without a full physical model.
      for (const [ratio, g] of [[1, 1], [2, 0.5], [3, 0.3], [4, 0.15]] as const) {
        if (freq * ratio > sampleRate / 2) continue;
        mixInto(buf, modalRing(sampleRate, freq * ratio, 3, seconds * 0.5, amplitude * g * 0.5, seconds), 0);
      }
      return lowpass(buf, 5200);
    }
    case 'drone': {
      const buf = createBuffer(sampleRate, seconds);
      mixInto(buf, shimmer(sampleRate, freq / 2, seconds, 9, amplitude * 0.7), 0);
      return lowpass(buf, 1400);
    }
    case 'percussion': {
      const buf = createBuffer(sampleRate, Math.min(seconds, 0.25));
      mixInto(buf, modalRing(sampleRate, rng.range(70, 110), 5, 0.06, amplitude, 0.2), 0);
      return buf;
    }
  }
}

/**
 * Render one track as a seamless loop.
 *
 * Structure: the motif cycles at the track's tempo; a bass drone holds the root;
 * percussion tracks add a pulse. Deliberately simple — the goal is a usable,
 * on-brand draft loop, not a finished orchestral cue.
 */
export function renderTrack(spec: TrackSpec, seed = 1, sampleRate = SAMPLE_RATE): Buffer {
  const rng = new NoiseRng(seed);
  const intervals = transformMotif(spec.transform);
  const beat = 60 / spec.bpm;
  const noteLen = spec.transform === 'augmented' ? beat * 4 : beat * 2;
  // Render extra tail so the loop cross-fade has real material to work with.
  const crossfade = Math.min(2.5, spec.loopSeconds * 0.08);
  const out = createBuffer(sampleRate, spec.loopSeconds + crossfade);
  const root = semitonesToHz(MOTIF_ROOT_HZ * 2, spec.transpose);

  const melodic = spec.instruments.filter((i) => i !== 'percussion' && i !== 'drone');
  const lead = melodic[0] ?? 'harp';
  const pad = melodic[1];

  let t = 0;
  let noteIndex = 0;
  while (t < out.data.length / sampleRate) {
    const semi = intervals[noteIndex % intervals.length]!;
    const freq = semitonesToHz(root, semi);
    const isPhraseStart = noteIndex % intervals.length === 0;

    mixInto(
      out,
      instrumentVoice(sampleRate, lead, freq, noteLen * 2.2, isPhraseStart ? 0.3 : 0.22, rng),
      t,
    );
    if (pad && isPhraseStart) {
      mixInto(out, instrumentVoice(sampleRate, pad, freq / 2, noteLen * intervals.length, 0.14, rng), t);
    }
    if (spec.transform === 'rhythmic') {
      // Staccato doubling on the off-beat gives combat its drive.
      mixInto(out, instrumentVoice(sampleRate, lead, freq * 2, noteLen * 0.35, 0.12, rng), t + noteLen * 0.5);
    }
    t += noteLen;
    noteIndex++;
  }

  if (spec.instruments.includes('drone')) {
    for (let d = 0; d < out.data.length / sampleRate; d += 8) {
      mixInto(out, instrumentVoice(sampleRate, 'drone', root, 9, 0.16, rng), d);
    }
  }
  if (spec.instruments.includes('percussion')) {
    for (let p = 0; p < out.data.length / sampleRate; p += beat) {
      const accent = Math.round(p / beat) % 4 === 0;
      mixInto(out, instrumentVoice(sampleRate, 'percussion', root, 0.2, accent ? 0.22 : 0.1, rng), p);
    }
  }
  if (spec.id === 'mus.mystery' || spec.id === 'mus.lastRoom') {
    // A reversed breath, once per loop, so the mystery tracks feel watched.
    const breath = noiseSweep(sampleRate, rng, 3.0, 400, 3000, 0.05);
    breath.data.reverse();
    mixInto(out, breath, spec.loopSeconds * 0.55);
  }

  lowpass(out, 9000);
  const looped = makeSeamlessLoop(out, crossfade);
  applyFades(looped, 0.02, 0.02);
  // −16 LUFS target (Audio Bible §6) approximated by peak headroom; a real
  // loudness pass belongs in the mastering step, not here.
  return normalize(looped, 0.55);
}

export function renderTrackById(id: string, seed = 1, sampleRate = SAMPLE_RATE): Buffer | null {
  const spec = TRACK_BY_ID.get(id);
  return spec ? renderTrack(spec, seed, sampleRate) : null;
}
